import { type FC, useState } from "react";
import { Box, LinearProgress, Slider } from "@mui/material";
import { formatPlayerTime } from "./formatPlayerTime";
import { TimecodeMarkers } from "./TimecodeMarkers";
import type { PlayerTimecode } from "./playerTimecode";

interface SeekBarProps {
  currentTime: number;
  duration: number;
  buffered: number;
  onSeek: (time: number) => void;
  /** Метки таймлайна (MARKER) — иконки поверх сикбара. */
  timecodes?: PlayerTimecode[] | null;
}

export const SeekBar: FC<SeekBarProps> = ({
  currentTime,
  duration,
  buffered,
  onSeek,
  timecodes,
}) => {
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);
  const bufferedPercent =
    duration > 0 ? Math.min(100, (buffered / duration) * 100) : 0;
  const sliderValue = dragValue ?? (duration > 0 ? currentTime : 0);

  return (
    <Box
      sx={{
        position: "relative",
        px: 1,
        height: 24,
        display: "flex",
        alignItems: "center",
      }}
      onMouseMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.max(
          0,
          Math.min(1, (event.clientX - rect.left) / rect.width)
        );
        setHoverTime(ratio * duration);
      }}
      onMouseLeave={() => setHoverTime(null)}
    >
      <LinearProgress
        variant="determinate"
        value={bufferedPercent}
        sx={{
          position: "absolute",
          left: 8,
          right: 8,
          height: 4,
          borderRadius: 2,
          backgroundColor: "transparent",
          "& .MuiLinearProgress-bar": {
            backgroundColor: "rgba(255,255,255,0.2)",
          },
        }}
      />
      {/* No `size="small"` here on purpose: the reference slider is
          default/medium-sized too (it only shrinks the thumb via its own
          class override), which keeps the thumb's default resting
          drop-shadow (`theme.shadows[2]`, suppressed by MUI's small-size
          variant) and its default 42px hit target. Only the visible
          dimensions are overridden below, matching reference exactly. */}
      <Slider
        value={sliderValue}
        min={0}
        max={duration || 1}
        step={0.1}
        onChange={(_event, value) => setDragValue(value as number)}
        onChangeCommitted={(_event, value) => {
          setDragValue(null);
          onSeek(value as number);
        }}
        valueLabelDisplay={hoverTime != null ? "on" : "off"}
        valueLabelFormat={() => formatPlayerTime(hoverTime ?? currentTime)}
        sx={{
          position: "relative",
          padding: "11px 0",
          // Reference never sets rail opacity itself — it relies on MUI's
          // own default (0.38) to dim the unplayed portion against the
          // played track/thumb. Our previous round explicitly forced
          // opacity:1 here, which killed that dimming and made the whole
          // bar read as one flat color; pin 0.38 explicitly instead of
          // silently depending on the MUI default.
          "& .MuiSlider-rail": {
            opacity: 0.38,
            height: 4,
            backgroundColor: "primary.dark",
          },
          "& .MuiSlider-track": {
            height: 4,
            backgroundColor: "primary.dark",
            border: "none",
          },
          "& .MuiSlider-thumb": {
            width: 10,
            height: 10,
            backgroundColor: "#fff",
          },
        }}
      />
      <TimecodeMarkers
        timecodes={timecodes}
        duration={duration}
        onTimecodeClick={onSeek}
      />
    </Box>
  );
};
